import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { RacingPostIcon } from 'src/domains/layouts/shared/assets/icons/RacingPostIcon';
import { Loader } from 'src/domains/layouts/webview/components/loader/Loader';
import { Link } from 'src/domains/layouts/webview/components/router/NewLink';
import { withConfig } from 'src/withConfig';

interface MeetingsListPropsType {
    isOpen: boolean;
    variant: DefaultComponentVariantType | null;
}

const setMeetingsListStyle = (variant: DefaultComponentVariantType | null, isOpen: boolean, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            display: ${isOpen ? 'flex' : 'none'};
            flex-flow: column nowrap;
            gap: ${theme.star.gap.small};
        `;
    }
    return css`
        background-color: ${theme.star.meetingsListColors.bgColorSecondary};
        display: ${isOpen ? 'block' : 'none'};
        @media ${theme.star.mediaQuery.tablet} {
            position: relative;
            &::before {
                content: '';
                position: absolute;
                border-top: 1px solid ${theme.star.meetingsRowColors.borderColor};
                left: 0;
                right: 0;
                top: 0;
                z-index: ${theme.star.zIndexGlobal.above};
            }
        }
    `;
};

export const MeetingsListStyle = withConfig(theme => styled('section', { label: 'MeetingsListStyle' })<MeetingsListPropsType>`
    ${({ variant, isOpen }): SerializedStyles => setMeetingsListStyle(variant, isOpen, theme)}
`);

interface MeetingsListInfoPropsType {
    variant: DefaultComponentVariantType | null;
}

const setMeetingsListInfo = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            border-radius: ${theme.star.radius.medium};
        `;
    }
    return css`
        border-color: ${theme.star.meetingsRowColors.borderColor};
        border-style: solid;
        border-width: 0 1px;
        margin: -1px 0 1px;
    `;
};

export const MeetingsListInfo = withConfig(theme => styled('div', { label: 'MeetingsListInfo' })<MeetingsListInfoPropsType>`
    ${({ variant }): SerializedStyles => setMeetingsListInfo(variant, theme)}
    background: ${theme.star.meetingsListColors.bgColorTertiary};
    color: ${theme.star.meetingsListColors.txtColor};
    font-size: ${theme.star.fontSize.xRegular};
    padding: 10px 5px;
    text-align: center;
`);

interface MeetingsEventLinkPropsType {
    isFinished: boolean;
    isInProgress: boolean;
    variant: DefaultComponentVariantType | null;
}

const setMeetingsEventLink = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            border-radius: ${theme.star.radius.small};
            height: 48px;
        `;
    }
    return css`
        height: 40px;
        @media ${theme.star.mediaQuery.tablet} {
            border-width: 0 1px 1px 0;
        }
    `;
};

export const MeetingsEventLink = withConfig(theme => styled(Link, { label: 'MeetingsEventLink' })<MeetingsEventLinkPropsType>`
    ${({ variant }): SerializedStyles => setMeetingsEventLink(variant, theme)}
    align-items: center;
    background-color: ${theme.star.meetingsRowColors.bgColor};
    border-color: ${theme.star.meetingsRowColors.borderColorSecondary};
    border-style: solid;
    border-width: 1px;
    color: ${({ isInProgress, isFinished }): string => isInProgress || isFinished ? theme.star.meetingsRowColors.txtColorSecondary : theme.star.meetingsRowColors.txtColor};
    display: flex;
    flex-flow: row nowrap;
    flex: 0 0 80px;
    gap: 3px;
    font-weight: ${theme.star.fontWeight.bold};
    justify-content: center;
    line-height: 1.4285;
    margin: -1px -1px -1px 0;
    padding: ${theme.star.padding.small};
    position: relative;
    text-align: center;
    text-decoration: none;
    white-space: nowrap;
    width: 80px;
    &:hover {
        opacity: 0.8;
    }
    @media ${theme.star.mediaQuery.tablet} {
        flex: 0 0 calc(100% / 8);
        gap: 5px;
        margin: 0 -1px 0 0;
        width: calc(100% / 8);
    }
`);

export const TimeData = withConfig(theme => styled('span', { label: 'TimeData' })`
    display: block;
    font-size: ${theme.star.fontSize.xRegular};
    max-width: 37px;
    width: 100%;
`);

export const RacingPostIconWrapper = withConfig(theme => styled(RacingPostIcon, { label: 'RacingPostIconWrapper' })`
    fill: ${theme.star.meetingsRowColors.bgColorSecondary};
    flex: 0 0 16px;
    width: 16px;
`);

export const LoaderWrapperMeetings = styled(Loader, { label: 'LoaderWrapperMeetings' })`
    justify-content: center;
`;
